import { useEffect, useState } from "react";

// Тема: light | dark | system. За замовчуванням — світла. Зберігається в браузері;
// "system" прибирає data-theme, і тоді діє prefers-color-scheme (styles.css)
const KEY = "etax.theme";
export const THEMES = [
  { key: "light", label: "Світла тема", icon: "sun" },
  { key: "dark", label: "Темна тема", icon: "moon" },
  { key: "system", label: "Системна", icon: "auto" },
];

export function getTheme() {
  try { return localStorage.getItem(KEY) || "light"; } catch { return "light"; }
}

export function applyTheme(t) {
  const el = document.documentElement;
  if (t === "system") el.removeAttribute("data-theme");
  else el.setAttribute("data-theme", t);
}

const ICONS = {
  sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2.5v2M12 19.5v2M4.6 4.6l1.4 1.4M18 18l1.4 1.4M2.5 12h2M19.5 12h2M4.6 19.4 6 18M18 6l1.4-1.4" /></>,
  moon: <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z" />,
  auto: <><rect x="3" y="4.5" width="18" height="12" rx="2" /><path d="M8.5 20h7M12 16.5V20" /></>,
};

// Тема й вибір теми — для меню користувача й перемикача на стартовій сторінці
export function useTheme() {
  const [theme, setTheme] = useState(getTheme);
  const choose = (t) => {
    setTheme(t);
    applyTheme(t);
    try { localStorage.setItem(KEY, t); } catch {}
  };
  return [theme, choose];
}

// Вигляд (skin) і його параметри — атрибути на <html>: data-skin, data-font, data-accent. Зберігаються в браузері так само,
// як тема; ?skin=&font=&accent= в адресі задають і запам'ятовують значення (для знімків і порівняння).
// За замовчуванням — світла тема, вигляд minimal, шрифт Onest, синьо-зелений акцент (рішення власника 21.10.2026);
// «Стандартний» і e-Ukraine лишаються в меню. Шрифт і акцент діють лише у вигляді minimal (web/src/design/skin-minimal.css)
export const LOOK = {
  skin: { key: "etax.skin", attr: "data-skin", def: "minimal", options: [
    { key: "default", label: "Стандартний" }, { key: "minimal", label: "Мінімалістичний" }] },
  font: { key: "etax.font", attr: "data-font", def: "onest", options: [
    { key: "onest", label: "Onest" }, { key: "e-ukraine", label: "e-Ukraine" }] },
  accent: { key: "etax.accent", attr: "data-accent", def: "teal", options: [
    { key: "green", label: "Зелений" }, { key: "teal", label: "Синьо-зелений" }] },
};
const LOOK_EVENT = "etax-look";

export function getLook(name) {
  const l = LOOK[name];
  let v = null;
  try { v = localStorage.getItem(l.key); } catch {}
  return l.options.some((o) => o.key === v) ? v : l.def;
}

function applyLookValue(name, v) {
  const l = LOOK[name];
  if (name === "skin" && v === "default") document.documentElement.removeAttribute(l.attr);
  else document.documentElement.setAttribute(l.attr, v);
}

export function setLook(name, v) {
  if (!LOOK[name].options.some((o) => o.key === v)) return;
  applyLookValue(name, v);
  try { localStorage.setItem(LOOK[name].key, v); } catch {}
  dispatchEvent(new CustomEvent(LOOK_EVENT, { detail: { name, value: v } }));
}

// До першого рендеру: параметри з адреси (якщо є) зберігаються, далі — збережені значення
export function applyLook() {
  let q = null;
  try { q = new URLSearchParams(location.search); } catch {}
  for (const name of Object.keys(LOOK)) {
    const v = q?.get(name);
    if (v && LOOK[name].options.some((o) => o.key === v)) { try { localStorage.setItem(LOOK[name].key, v); } catch {} }
    applyLookValue(name, getLook(name));
  }
}

// Поточне значення параметра вигляду; оновлюється, коли його змінили в меню
export function useLook(name) {
  const [v, setV] = useState(() => getLook(name));
  useEffect(() => {
    const on = (e) => { if (e.detail?.name === name) setV(e.detail.value); };
    addEventListener(LOOK_EVENT, on);
    return () => removeEventListener(LOOK_EVENT, on);
  }, [name]);
  return [v, (x) => setLook(name, x)];
}

export function ThemeToggle() {
  const [theme, choose] = useTheme();
  return (
    <div className="theme-toggle" role="group" aria-label="Тема оформлення">
      {THEMES.map((t) => (
        <button key={t.key} type="button" className={theme === t.key ? "active" : ""} aria-pressed={theme === t.key}
          title={t.label} aria-label={t.label} onClick={() => choose(t.key)}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICONS[t.icon]}</svg>
        </button>
      ))}
    </div>
  );
}
